import { useCallback, useEffect, useRef, useState } from 'react';
import { listHitlTasks, resolveHitlTask } from '@aimsos/chat';
import { api } from '../../../services/apiClient.js';

/**
 * Data hook for the HITL task inbox: paginated list + resolve.
 *
 * `refresh` is exposed so the page can re-fetch after a decision lands —
 * the resolved task must leave the pending list immediately.
 */
export const useHitlTasks = ({ status = 'pending', pageSize = 20 } = {}) => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolving, setResolving] = useState(false);
  // Mirrors `status` so an in-flight fetch can tell whether the filter moved
  // on while it was awaiting — a slow "pending" response must not overwrite a
  // fast "resolved" one (or vice versa) after a quick SegmentedControl switch.
  const statusRef = useRef(status);
  statusRef.current = status;

  const fetchTasks = useCallback(
    async (targetPage = page) => {
      const requestStatus = status;
      setLoading(true);
      setError(null);
      try {
        const { items: rows, meta: pagination } = await listHitlTasks(api, {
          status: requestStatus,
          page: targetPage,
          pageSize,
        });
        // Drop the response if `status` moved on while this call was in flight.
        if (requestStatus !== statusRef.current) return;
        setItems(rows);
        setMeta(pagination);
      } catch (err) {
        if (requestStatus !== statusRef.current) return;
        setError(err);
        setItems([]);
      } finally {
        if (requestStatus === statusRef.current) setLoading(false);
      }
    },
    [status, page, pageSize],
  );

  useEffect(() => {
    setPage(1);
    fetchTasks(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const goToPage = useCallback(
    (nextPage) => {
      setPage(nextPage);
      fetchTasks(nextPage);
    },
    [fetchTasks],
  );

  const resolve = useCallback(
    async ({ contractId, decision }) => {
      setResolving(true);
      try {
        const result = await resolveHitlTask(api, { contractId, decision });
        await fetchTasks(page);
        return result;
      } finally {
        setResolving(false);
      }
    },
    [fetchTasks, page],
  );

  return {
    items,
    meta,
    page,
    loading,
    error,
    resolving,
    goToPage,
    refresh: () => fetchTasks(page),
    resolve,
  };
};

export default useHitlTasks;
